import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Download, ImagePlus } from "lucide-react";
import { api, imgSrc, formatApiError } from "@/lib/api";
import { useSeo, GALLERY_CATEGORIES } from "@/lib/site";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_STYLES = {
  new: "bg-terracotta text-white",
  contacted: "bg-amber-100 text-amber-800",
  quoted: "bg-blue-100 text-blue-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-neutral-200 text-neutral-600",
};

export default function AdminDashboard() {
  useSeo("Dashboard — Breeze Craft Admin", "");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const loadLeads = () => api.get("/admin/leads", { params: filter ? { status: filter } : {} }).then((r) => setLeads(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadBlocks = () => api.get("/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  const loadGallery = () => api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});

  useEffect(() => { if (user) { loadLeads(); loadBlocks(); loadGallery(); } }, [user, filter]); // eslint-disable-line

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("ba_token");
    navigate("/admin/login");
  };

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/leads-export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "breeze-art-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user) return <div className="min-h-screen bg-bone flex items-center justify-center text-sm text-neutral-500" data-testid="admin-loading">Loading…</div>;

  const counts = leads.length;

  return (
    <div className="min-h-screen bg-bone-dark" data-testid="admin-dashboard">
      <header className="bg-ink text-bone">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-xl font-bold">Breeze Craft</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">Admin</span>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm text-bone/70 hover:text-terracotta transition-colors">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2 border-b border-ink/10 pb-0" data-testid="admin-tabs">
          {[["leads", `Leads${tab === "leads" ? ` (${counts})` : ""}`], ["catalogue", "Catalogue"], ["gallery", "Gallery"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${tab === id ? "text-terracotta border-b-2 border-terracotta" : "text-neutral-500 hover:text-ink"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div className="mt-6" data-testid="admin-leads-panel">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <select
                data-testid="admin-leads-filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-ink/15 bg-white px-3 py-2 text-sm"
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={exportCsv} data-testid="admin-export-csv-button" className="inline-flex items-center gap-2 border border-ink/15 bg-white px-4 py-2 text-sm hover:border-terracotta hover:text-terracotta transition-colors">
                <Download size={14} /> Export CSV
              </button>
            </div>
            {leads.length === 0 ? (
              <p className="text-sm text-neutral-500 py-10" data-testid="admin-leads-empty">No leads yet.</p>
            ) : (
              <div className="space-y-2">
                {leads.map((l) => (
                  <div key={l.id} className="bg-white border border-ink/10" data-testid={`admin-lead-${l.id.slice(0, 8)}`}>
                    <button onClick={() => setExpanded(expanded === l.id ? null : l.id)} className="w-full px-4 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-left">
                      <span className={`px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider ${STATUS_STYLES[l.status]}`} data-testid={`lead-status-${l.id.slice(0, 8)}`}>{l.status}</span>
                      <span className="font-medium text-sm text-ink">{l.name}</span>
                      <span className="text-xs text-neutral-500">{l.suburb} · {l.project_type} · {l.service_level}</span>
                      <span className="ml-auto font-mono text-[10px] text-neutral-400">{new Date(l.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
                    </button>
                    {expanded === l.id && (
                      <div className="px-4 pb-4 pt-1 border-t border-ink/5 text-sm" data-testid={`lead-detail-${l.id.slice(0, 8)}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-neutral-600">
                          <p><span className="text-neutral-400">Email:</span> <a className="text-terracotta" href={`mailto:${l.email}`}>{l.email}</a></p>
                          <p><span className="text-neutral-400">Phone:</span> <a className="text-terracotta" href={`tel:${l.phone}`}>{l.phone}</a></p>
                          <p><span className="text-neutral-400">Block:</span> {l.block_interest || "—"}</p>
                          <p><span className="text-neutral-400">Size:</span> {l.size || "—"}</p>
                          <p><span className="text-neutral-400">Budget:</span> {l.budget || "—"}</p>
                          <p><span className="text-neutral-400">Timeframe:</span> {l.timeframe || "—"}</p>
                        </div>
                        {l.message && <p className="mt-2 text-neutral-600"><span className="text-neutral-400">Message:</span> {l.message}</p>}
                        {l.photos?.length > 0 && (
                          <div className="mt-3 flex gap-2 flex-wrap">
                            {l.photos.map((p) => (
                              <a key={p} href={imgSrc(p)} target="_blank" rel="noreferrer" data-testid={`lead-photo-${p.slice(-8)}`}>
                                <img src={imgSrc(p)} alt="Site" className="w-20 h-20 object-cover border border-ink/10" />
                              </a>
                            ))}
                          </div>
                        )}
                        <div className="mt-4 flex flex-wrap gap-2">
                          {STATUSES.filter((s) => s !== l.status).map((s) => (
                            <button key={s} data-testid={`lead-mark-${s}-${l.id.slice(0, 8)}`} onClick={() => setStatus(l, s)} className="border border-ink/15 px-3 py-1.5 text-xs font-medium hover:border-terracotta hover:text-terracotta transition-colors">
                              Mark {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "catalogue" && <CatalogueAdmin blocks={blocks} reload={loadBlocks} />}
        {tab === "gallery" && <GalleryAdmin gallery={gallery} reload={loadGallery} />}
      </div>
    </div>
  );
}

function Uploader({ onUploaded, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (files) => {
    setBusy(true);
    for (const f of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads", fd);
        onUploaded(r.data.path);
      } catch (e) { toast.error(formatApiError(e)); }
    }
    setBusy(false);
  };
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 border border-dashed border-ink/30 px-4 py-2.5 text-sm text-neutral-500 hover:border-terracotta hover:text-terracotta transition-colors">
        <ImagePlus size={15} /> {busy ? "Uploading…" : "Upload image"}
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => { handle(e.target.files); e.target.value = ""; }} />
    </>
  );
}

const inp = "w-full border border-ink/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-terracotta";

function CatalogueAdmin({ blocks, reload }) {
  const empty = { slug: "", name: "", brand: "", pattern: "", dimensions: "", finish: "", colours: "", typical_uses: "", description: "", images: [], order: 99 };
  const [form, setForm] = useState(null);

  const save = async () => {
    try {
      await api.post("/admin/blocks", {
        ...form,
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        colours: typeof form.colours === "string" ? form.colours.split(",").map((s) => s.trim()).filter(Boolean) : form.colours,
        typical_uses: typeof form.typical_uses === "string" ? form.typical_uses.split(",").map((s) => s.trim()).filter(Boolean) : form.typical_uses,
        order: Number(form.order) || 99,
      });
      toast.success("Block saved");
      setForm(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-6" data-testid="admin-catalogue-panel">
      <button onClick={() => setForm(empty)} data-testid="admin-block-add-button" className="inline-flex items-center gap-2 bg-ink text-bone px-4 py-2.5 text-sm font-medium hover:bg-terracotta transition-colors">
        <Plus size={15} /> New block
      </button>
      {form && (
        <div className="mt-5 bg-white border border-ink/10 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-block-form">
          <input className={inp} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="block-form-name" />
          <input className={inp} placeholder="Slug (auto if blank)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          <input className={inp} placeholder="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
          <input className={inp} placeholder="Pattern" value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} />
          <input className={inp} placeholder="Dimensions e.g. 290 × 290 × 90 mm" value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} />
          <input className={inp} placeholder="Finish" value={form.finish} onChange={(e) => setForm({ ...form, finish: e.target.value })} />
          <input className={inp} placeholder="Colours (comma separated)" value={form.colours} onChange={(e) => setForm({ ...form, colours: e.target.value })} />
          <input className={inp} placeholder="Typical uses (comma separated)" value={form.typical_uses} onChange={(e) => setForm({ ...form, typical_uses: e.target.value })} />
          <textarea className={inp + " sm:col-span-2"} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
            <Uploader testid="block-form-upload" onUploaded={(p) => setForm((f) => ({ ...f, images: [...f.images, p] }))} />
            {form.images.map((p) => <img key={p} src={imgSrc(p)} alt="" className="w-14 h-14 object-cover border border-ink/10" />)}
          </div>
          <div className="sm:col-span-2 flex gap-3">
            <button onClick={save} data-testid="block-form-save" className="bg-terracotta text-white px-5 py-2.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors">Save block</button>
            <button onClick={() => setForm(null)} className="border border-ink/15 px-5 py-2.5 text-sm">Cancel</button>
          </div>
        </div>
      )}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {blocks.map((b) => (
          <div key={b.slug} className="bg-white border border-ink/10 p-4 flex gap-4" data-testid={`admin-block-${b.slug}`}>
            <img src={imgSrc(b.images[0])} alt="" className="w-16 h-16 object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-ink">{b.name}</p>
              <p className="text-xs text-neutral-500 truncate">{b.dimensions}</p>
              <div className="mt-2 flex gap-2">
                <button onClick={() => setForm({ ...b, colours: b.colours.join(", "), typical_uses: b.typical_uses.join(", ") })} data-testid={`admin-block-edit-${b.slug}`} className="text-xs text-terracotta font-medium">Edit</button>
                <button onClick={async () => { await api.delete(`/admin/blocks/${b.slug}`); reload(); }} data-testid={`admin-block-delete-${b.slug}`} className="text-xs text-neutral-400 hover:text-red-600 inline-flex items-center gap-1"><Trash2 size={11} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryAdmin({ gallery, reload }) {
  const empty = { title: "", suburb: "", category: "Feature Walls", block_name: "", image: "", description: "", order: 99 };
  const [form, setForm] = useState(null);

  const save = async () => {
    try {
      if (form.id) await api.put(`/admin/gallery/${form.id}`, form);
      else await api.post("/admin/gallery", form);
      toast.success("Gallery item saved");
      setForm(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-6" data-testid="admin-gallery-panel">
      <button onClick={() => setForm(empty)} data-testid="admin-gallery-add-button" className="inline-flex items-center gap-2 bg-ink text-bone px-4 py-2.5 text-sm font-medium hover:bg-terracotta transition-colors">
        <Plus size={15} /> New project
      </button>
      {form && (
        <div className="mt-5 bg-white border border-ink/10 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-gallery-form">
          <input className={inp} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gallery-form-title" />
          <input className={inp} placeholder="Suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} />
          <select className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="gallery-form-category">
            {GALLERY_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className={inp} placeholder="Block used" value={form.block_name} onChange={(e) => setForm({ ...form, block_name: e.target.value })} />
          <textarea className={inp + " sm:col-span-2"} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="sm:col-span-2 flex items-center gap-3">
            <Uploader testid="gallery-form-upload" onUploaded={(p) => setForm((f) => ({ ...f, image: p }))} />
            {form.image && <img src={imgSrc(form.image)} alt="" className="w-14 h-14 object-cover border border-ink/10" />}
          </div>
          <div className="sm:col-span-2 flex gap-3">
            <button onClick={save} data-testid="gallery-form-save" className="bg-terracotta text-white px-5 py-2.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors">Save project</button>
            <button onClick={() => setForm(null)} className="border border-ink/15 px-5 py-2.5 text-sm">Cancel</button>
          </div>
        </div>
      )}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {gallery.map((g) => (
          <div key={g.id} className="bg-white border border-ink/10 p-4 flex gap-4" data-testid={`admin-gallery-item-${g.id.slice(0, 8)}`}>
            <img src={imgSrc(g.image)} alt="" className="w-16 h-16 object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-ink truncate">{g.title}</p>
              <p className="text-xs text-neutral-500">{g.suburb} · {g.category}</p>
              <div className="mt-2 flex gap-2">
                <button onClick={() => setForm(g)} data-testid={`admin-gallery-edit-${g.id.slice(0, 8)}`} className="text-xs text-terracotta font-medium">Edit</button>
                <button onClick={async () => { await api.delete(`/admin/gallery/${g.id}`); reload(); }} data-testid={`admin-gallery-delete-${g.id.slice(0, 8)}`} className="text-xs text-neutral-400 hover:text-red-600 inline-flex items-center gap-1"><Trash2 size={11} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
